import { useMemo } from "react";
import { Link } from "react-router";
import { TrendingUp } from "lucide-react";
import { ArticleImage } from "./ArticleCard";
import { type Article } from "@/data/articles";
import { useQueryClient } from "@tanstack/react-query";
import { fetchArticle } from "@/hooks/useArticlesData";
import { Skeleton } from "@/components/ui/skeleton";
import { getCategoryTheme } from "@/lib/categoryTheme";
import { CategoryBadge } from "@/components/ui/CategoryBadge";

export function MostReadSection({ articles }: { articles: Article[] }) {
  // Rank by actual recorded views (articles.view_count). Ties and zero-view
  // articles fall back to the incoming (newest-first) order so the section still
  // fills before any views exist.
  const mostRead = useMemo(
    () =>
      [...articles]
        .sort((a, b) => (b.viewCount ?? 0) - (a.viewCount ?? 0))
        .slice(0, 5),
    [articles],
  );

  const queryClient = useQueryClient();

  if (mostRead.length < 3) return null;

  return (
    <section className="mb-10 py-8 bg-muted/50">
      <div className="container">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <TrendingUp className="h-8 w-8 md:h-10 md:w-10 text-primary shrink-0" />
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-2">
              <span className="text-primary font-bold">/</span>
              <h2 className="text-lg md:text-xl font-bold text-foreground leading-tight">
                Most Read
              </h2>
              <span className="text-primary font-bold">/</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Top stories right now
            </p>
          </div>
        </div>

        {/* Numbered article list — responsive mobile & 4K grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-3.5 sm:gap-4">
          {mostRead.map((article, i) => (
            <Link
              key={article.id}
              to={`/article/${article.slug}`}
              style={{ "--hover-color": getCategoryTheme(article.category || article.categoryLabel).color } as React.CSSProperties}
              onMouseEnter={() => {
                queryClient.prefetchQuery({
                  queryKey: ["article", article.slug],
                  queryFn: () => fetchArticle(article.slug),
                  staleTime: 1000 * 60 * 5,
                });
              }}
              onFocus={() => {
                queryClient.prefetchQuery({
                  queryKey: ["article", article.slug],
                  queryFn: () => fetchArticle(article.slug),
                  staleTime: 1000 * 60 * 5,
                });
              }}
              className="group flex items-center gap-2.5 sm:gap-4 p-2.5 sm:p-3 rounded-xl hover:bg-background/70 transition-colors duration-200"
            >
              {/* Rank number */}
              <span className="text-2xl sm:text-3xl md:text-4xl font-black text-muted-foreground/20 leading-none w-7 sm:w-10 shrink-0 select-none tabular-nums text-center">
                {String(i + 1).padStart(2, "0")}
              </span>

              {/* Thumbnail */}
              <div className="w-16 h-12 sm:w-20 sm:h-14 rounded-lg overflow-hidden flex-shrink-0 relative bg-muted">
                <ArticleImage
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05] will-change-transform"
                />
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <CategoryBadge
                  category={article.category || article.categoryLabel || "general"}
                  label={article.categoryLabel}
                  variant="solid"
                  size="xs"
                  showIcon={false}
                  className="mb-1"
                />
                <h3 className="text-xs sm:text-sm font-semibold text-foreground leading-snug line-clamp-2 group-hover:text-[var(--hover-color)] transition-colors duration-200">
                  {article.title}
                </h3>
                <span className="text-xs text-muted-foreground mt-0.5 block">
                  {article.date}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function MostReadSkeleton() {
  return (
    <section className="mb-10 py-8 bg-muted/50">
      <div className="container">
        <div className="flex items-center gap-3 mb-6">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-1.5">
            <Skeleton className="h-5 w-32 rounded" />
            <Skeleton className="h-3 w-24 rounded" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-3.5 sm:gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 bg-card rounded-xl border border-border/50">
              <Skeleton className="w-6 sm:w-7 h-7 sm:h-8 rounded shrink-0" />
              <Skeleton className="w-16 h-12 sm:w-20 sm:h-14 rounded-lg shrink-0" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-14 rounded" />
                <Skeleton className="h-3.5 w-full rounded" />
                <Skeleton className="h-2.5 w-16 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
